import { IsInterface, isNullable, isString } from "generic-type-guard";

export const isLocalizedMessage = new IsInterface()
	.withProperties({
		key: isString,
		default: isString
	})
	.get();
export const isErrorDetails = new IsInterface()
	.withProperties({
		code: isString,
		subsystem: isString,
		time: isString
	})
	.get();
export const isRpcErrorData = new IsInterface()
	.withProperties({
		level: isString,
		title: isLocalizedMessage,
		description: isLocalizedMessage,
		details: isErrorDetails,
		source: isString,
		timestamp: isString,
		logId: isString,
		stacktrace: isNullable(isString),
		exception: isNullable(isString)
	})
	.get();
export const isRpcErrorResponse = new IsInterface()
	.withProperties({
		data: isRpcErrorData
	})
	.get();
export function extractErrorMessage(error: unknown): string | undefined {
	return isRpcErrorResponse(error) ? error.data.description.default : undefined;
}
/**
 * Detects whether an error message from the Eldorado backend indicates an invalid Aktenzeichen.
 * The Eldorado SOAP service returns messages like "Hierarchical with Alias '...' not found"
 * when the given Aktenzeichen does not exist. This function translates such raw messages
 * to a user-friendly German text.
 */
export function translateEldoradoCause(cause: string): string {
	if (cause.includes("not found") && cause.includes("Alias")) {
		return "Aktenzeichen ist ungültig.";
	}
	return cause;
}

/**
 * Extracts a human-readable cause string from any thrown error value.
 * Returns undefined when no meaningful message can be found.
 */
export function extractCause(error: unknown): string | undefined {
	if (error instanceof Error) {
		return error.message;
	}
	if (typeof error === "string") {
		return error;
	}
	if (error !== null && typeof (error as Record<string, unknown>).message === "string") {
		return (error as Record<string, unknown>).message as string;
	}
	return undefined;
}

/**
 * Extracts the HTTP status code from an Error whose message follows the pattern "NNN: ...".
 */
export function extractHttpStatusCode(error: unknown): number | undefined {
	if (error instanceof Error) {
		const match = /^(\d{3}):/.exec(error.message);
		if (match) {
			return parseInt(match[1], 10);
		}
	}
	return undefined;
}

/**
 * Returns true when the cause string is a raw HTTP status line (e.g. "500: Internal Server Error")
 * that should not be shown verbatim to the user.
 */
export function isRawHttpStatusCause(cause: string | undefined): boolean {
	return cause !== undefined && /^\d{3}:/.test(cause.trim());
}
